// Client-side mirror of a server-driven game. The server owns the simulation and
// streams sparse state deltas; we keep a local gameState and render it slightly in
// the past so there's always known trajectory data to interpolate across (worms
// move along smooth math trajectories, so a fixed delay hides network jitter).
import * as trajectoryUtil from "core/src/core/geometry/trajectory/trajectory-util.js";
import * as gsf from "core/src/core/game-state-functions.js";
import * as respawn from "core/src/core/respawn.js";

// How far behind the latest server time we render, in seconds (default). Must
// comfortably exceed the server's client-update interval (45ms) plus typical
// jitter so the render time never overruns the newest segment we've received.
// Tunable per-session (see createRemoteGame) so we can dial in smoothness.
const DEFAULT_RENDER_DELAY = 0.1;

export interface RemoteGame {
  // Seed from a `game_start` message (a full gameState for a new round).
  setInitial(gameState: any): void;
  // Merge a `game_update` delta (getGameStateChanges() output) in place.
  applyUpdate(data: any): void;
  // Estimated server game time to render at, kept renderDelay behind the server.
  getRenderTime(): number;
  // Estimated current server game time (no delay), the prediction target.
  getServerTime(): number;
  // Mark the local player so the client owns (and the server's copy is ignored) for
  // that worm's path under client-authoritative prediction.
  setLocalPlayerId(id: any): void;
  // Same, for several local players sharing this client (two-on-one-device online).
  setLocalPlayerIds(ids: any[]): void;
  getGameState(): any;
  // Debug: the server's latest authoritative path segment for a worm (any worm,
  // including your own). Follow it at getServerTime() to get server-truth position.
  getServerLastSegment(playerId: any): any;
  // Live render-delay tuning (admin experiment): change how far behind the server
  // the world renders, mid-game. Affects opponents/streamed state only - the own
  // worm is predicted at server time regardless.
  setRenderDelay(seconds: number): void;
  getRenderDelay(): number;
}

export function createRemoteGame(initialRenderDelay: number = DEFAULT_RENDER_DELAY): RemoteGame {
  let renderDelay = initialRenderDelay;
  let gameState: any = null;
  // Anchor: at wall-clock `anchorWall` the server's game time was `anchorServerTime`.
  // We extrapolate forward from the most recent anchor between updates.
  let anchorServerTime = 0;
  let anchorWall = Date.now();
  // When set (client-authoritative prediction), the server's copy of THESE players'
  // worms is ignored - the client owns those worms' paths (see prediction.ts). One
  // client can own several worms when two people share a device online.
  let localPlayerIds = new Set<any>();
  // Debug: the server's latest authoritative segment for EVERY worm, including your
  // own (which we otherwise discard). The ghost overlay follows these at server-now
  // to draw where the server actually has each worm, vs your delayed/predicted view.
  let serverLastSegment = new Map<any, any>();

  function setLocalPlayerId(id: any) {
    localPlayerIds = new Set(id == null ? [] : [id]);
  }
  function setLocalPlayerIds(ids: any[]) {
    localPlayerIds = new Set(ids || []);
  }

  function setInitial(gs: any) {
    // Core's effect code runs here for our own worm, and some of it (the drunk wobble)
    // draws from gameState.seed; core's random throws on anything but a 32 bit
    // integer. The host sends one, but never depend on it: a seed of our own is just
    // as good (our worm's randomness is ours alone).
    if (!Number.isInteger(gs.seed) || gs.seed < -2147483648 || gs.seed > 2147483647) gs.seed = (Math.random() * 0x100000000) | 0;
    gameState = gs;
    anchorServerTime = gs.gameTime || 0;
    anchorWall = Date.now();
    serverLastSegment = new Map();
  }

  function applyUpdate(data: any) {
    if (!gameState || !data) return;
    // The host streams segments in the compact wire form (core encode/decode).
    data = gsf.decodeGameStateChanges(data);
    const tracks = data.wormPathSegments || {};
    // Per worm track, replace from fromIndex onward: the boundary segment may have
    // been extended in place since the last send, and newer segments are appended.
    Object.keys(tracks).forEach((trackId) => {
      const { fromIndex, segments } = tracks[trackId];
      // Capture the server's latest segment for EVERY worm (incl. your own, which
      // we discard below) so the debug ghost can show server-truth positions.
      if (segments.length && segments[0]) serverLastSegment.set(segments[0].playerId, segments[segments.length - 1]);
      // Skip a local player's own worm - the client owns its path, the server's version would fight
      // (and pull) the client-authoritative one. This includes a full resync (fromIndex 0): we must
      // NOT let the server's copy replace a worm we still predict (that wipes our finer history and
      // snaps the head). Ownership is resolved from the worm's CURRENT playerId (not segments[0], its
      // ORIGINAL owner), since a switcharoonie hands a worm to a new player mid-track - so a worm we
      // just LOST is no longer "own" and DOES take the resync, while the one we GAINED keeps predicting.
      if (segments[0]) {
        const liveWorm = gsf.getWorm(gameState, segments[0].wormId);
        const ownerNow = liveWorm ? liveWorm.playerId : segments[0].playerId;
        if (localPlayerIds.has(ownerNow)) return;
      }
      // The server stamps a client-controlled (human) worm's last segment endTime
      // forward each tick so collision keeps running, without touching its geometry
      // or duration. We render by comparing segment endTime to the render clock, so
      // those inflated endTimes stall the renderer and break the trail into gaps. The
      // client never uses endTime for collision, so restore each segment's real
      // geometric end (startTime + duration) for correct, gap-free rendering.
      for (let i = 0; i < segments.length; i++) {
        const s = segments[i];
        if (typeof s.startTime === "number" && typeof s.duration === "number") {
          s.endTime = s.startTime + s.duration;
        }
      }
      let arr = gameState.wormPathSegments[trackId];
      if (!arr) arr = gameState.wormPathSegments[trackId] = [];
      // Apply the delta by TIME (append-only), not by the server's INDEX. An index splice is exact
      // only when our copy of the track holds the SAME segments as the server's. For a worm we just
      // LOST in a switcharoonie we keep our own, finer-grained predicted trail, so the server's index
      // points at a different spot in our array and splicing there would overwrite our old, already-
      // rendered segments. Instead: keep every local segment strictly BEFORE the delta's first segment
      // and append the delta. Old segments are never touched, only new ones are added - and for an
      // aligned opponent track (built entirely from the server stream) this is identical to the old
      // index splice. This is what lets the server drop its old force-resync entirely.
      if (segments.length) {
        const firstStart = segments[0].startTime;
        let keep = arr.length;
        while (keep > 0 && arr[keep - 1].startTime >= firstStart - 1e-9) keep--;
        arr.length = keep;
        for (let i = 0; i < segments.length; i++) arr.push(segments[i]);
      } else {
        arr.splice(fromIndex, Infinity);
      }
    });
    // Event arrays are append-only; the delta carries only the new ones.
    if (data.gameEvents) {
      gameState.gameEvents.push(...data.gameEvents);
      // The server streams only events, never the worms array, so death is never
      // reflected onto the worm/player objects. Apply each player_died here so the
      // dead worm stops rendering and the alive count + round winner are right. This
      // includes your OWN worm, which is otherwise frozen alive=true from game_start
      // (the "always wins / never dies" bug) - the client owns its PATH, but death is
      // server-authoritative.
      for (const e of data.gameEvents) {
        // A crash during the cosmetic victory lap must NOT change standings: the round is
        // already scored, so skip marking anyone out (and the snap) - it would grey the
        // winner's name for a lap wipeout. The worm still stops (own worm via its local
        // collision; others via their path simply ending). The event itself stays in
        // gameEvents: the renderers' celebration crash sequence and the crash SFX read it.
        // A Respawn round (core respawn.js): a colour leaves the board (mark it, as
        // the host did), and a worm is put in. The worms array only comes with
        // game_start, so make the new worm here, like the twin's below; its owner
        // (the predictor) takes it from there.
        if (e && e.type === "trail_cleared") respawn.applyTrailClear(gameState, e.playerId, e.time);
        if (e && e.type === "worm_spawned") {
          // (someone who joined the round mid-way is new to our copy of it)
          if (gameState.players && !gameState.players.some((pl: any) => pl.id === e.playerId)) gameState.players.push({ id: e.playerId, alive: true, steering: 0, gameTimeWhenSteeringChanged: e.time });
          const p = gameState.players && gameState.players.find((pl: any) => pl.id === e.playerId);
          if (p) p.alive = true;
          if (!gameState.worms.some((w: any) => w.id === e.wormId)) {
            const prev = gameState.worms.find((w: any) => w.playerId === e.playerId) || {};
            gameState.worms.push({
              ...prev, id: e.wormId, playerId: e.playerId, trackId: e.trackId || `${e.playerId}_${e.wormId}`, alive: true,
              centerX: e.x, centerY: e.y, direction: e.direction, radius: e.radius,
              speed: e.speed ?? prev.speed, turningSpeed: e.turningSpeed ?? prev.turningSpeed,
              // jumps are the owner's (its predictor's slot), never this object's
              jump: { remainingJumpTime: 0, timeSinceLastJump: 0 },
            });
          }
        }
        if (e && e.type === "player_died" && !data.victoryLap) {
          for (const w of gameState.worms) if (w.playerId === e.playerId) w.alive = false;
          const p = gameState.players && gameState.players.find((pl: any) => pl.id === e.playerId);
          if (p) p.alive = false;
          // For a LOCAL worm the client owns and predicts ahead: on a server-assigned
          // death (die-in-the-open), our predicted head has over-run past the real
          // crash point. Snap the trail back to the server's authoritative crash
          // POSITION so it freezes exactly where the server killed us, not where we
          // mispredicted to (and not "half-way", which clamping by the server's
          // tick-stamped death time produces). No-op when we already stopped at/before
          // that point (self-detected crash), since there's nothing past it to trim.
          if (localPlayerIds.has(e.playerId)) {
            // the worm that died (a Respawn round leaves a player several tracks)
            const died = data.gameEvents.find((d: any) => d && d.type === "worm_died" && d.playerId === e.playerId && d.time === e.time);
            const dw = died && gameState.worms.find((w: any) => w.id === died.wormId);
            snapOwnPathToServerDeath(e.playerId, e.time, dw ? dw.trackId : undefined);
          }
        }
      }
    }
    if (data.powerUpEvents) gameState.powerUpEvents.push(...data.powerUpEvents);
    if (data.effectEvents) {
      gameState.effectEvents.push(...data.effectEvents);
      // The twin power-up spawns a second worm server-side, but the worms array is only
      // sent at game_start, so other clients never get the twin's worm object - they'd
      // draw its trail (from the streamed track) but NOT its head (the head renderer
      // needs the worm object). Synthesize it from the "twin" effect event by cloning
      // its already-present partner (same player, colour, radius). The owning client
      // also makes one in its predictor; both are guarded, so no duplicate.
      for (const ev of data.effectEvents) {
        if (ev.type !== "spawn" || !ev.effect || ev.effect.type !== "twin") continue;
        const a = ev.effect.wormId;
        const tw = ev.effect.twinWormId;
        const wormA = gameState.worms.find((w: any) => w.id === a);
        const wormTw = gameState.worms.find((w: any) => w.id === tw);
        if (wormA && !wormTw && tw != null) gameState.worms.push({ ...wormA, id: tw, alive: true });
        else if (wormTw && !wormA && a != null) gameState.worms.push({ ...wormTw, id: a, alive: true });
      }
    }
    gameState.gameTime = data.gameTime;
    // Re-anchor the clock WITHOUT snapping: hard-resetting anchorServerTime to the
    // freshly received gameTime every update made the extrapolated clock jump a few
    // ms each time, which the eye reads as micro-stutter on your own worm. Instead
    // keep the smoothly-running estimate and nudge it a fraction toward the measured
    // value (snap only on a big desync, e.g. after a stall / tab background).
    const nowWall = Date.now();
    const estimated = anchorServerTime + (nowWall - anchorWall) / 1000;
    const error = data.gameTime - estimated;
    if (error > 0.25 || error < -0.25) {
      anchorServerTime = data.gameTime; // big desync: snap
    } else {
      anchorServerTime = estimated + error * 0.05; // gentle correction, no visible jump
    }
    anchorWall = nowWall;
  }

  // Snap a local worm's over-predicted trail back to the server's authoritative
  // CRASH POSITION. The server stamps a dying client-worm's marker at the current
  // tick time (ahead of the real contact), so clamping the local path by that time
  // lands the head short of / past the real spot ("half-way back"). Instead we take
  // the server's last-known head position for this worm and cut our own path at the
  // segment that actually reached it, so the head freezes exactly where the server
  // says we died. Falls back to time-based truncation when we have no server sample.
  function snapOwnPathToServerDeath(playerId: any, deathTime: number, onlyTrack?: string) {
    const sv = serverLastSegment.get(playerId);
    // The server's death marker for our worm carries the authoritative crash POSITION
    // in endX/endY, but its time is unreliable (a worm_died marker has zero duration and
    // a tick-stamped startTime). In "die in the open" our path keeps predicting to ~now
    // while the server is still adopting our segments a round-trip behind, so the over-run
    // is in POSITION, not in time past the death event. Clamping by time barely trims (the
    // "half-way" snap). Instead, find where our own path actually reached the server's
    // crash point (it passes exactly through it, being our own past position) and cut
    // there. Fall back to a plain time-clamp if we can't place it.
    if (!sv || typeof sv.endX !== "number" || typeof sv.endY !== "number") return truncateOwnPath(playerId, deathTime, onlyTrack);
    const tx = sv.endX;
    const ty = sv.endY;
    for (const trackId of Object.keys(gameState.wormPathSegments)) {
      const segs = gameState.wormPathSegments[trackId];
      if (onlyTrack !== undefined && trackId !== onlyTrack) continue;
      if (!segs || !segs.length || segs[0].playerId !== playerId) continue;
      // Nearest local segment end to the crash point, scanned back from the head and
      // bounded to a window around the death so a looping path can't match an earlier pass.
      let bestI = -1;
      let bestD = Infinity;
      for (let i = segs.length - 1; i >= 0; i--) {
        const s = segs[i];
        if (s.endTime < deathTime - 1.0) break;
        if (s.startTime > deathTime + 0.2) continue;
        const dx = (s.endX ?? 0) - tx;
        const dy = (s.endY ?? 0) - ty;
        const d = dx * dx + dy * dy;
        if (d < bestD) { bestD = d; bestI = i; }
      }
      // If the crash point isn't on our recent path, don't snap somewhere wrong.
      if (bestI < 0 || bestD > 15 * 15) return truncateOwnPath(playerId, deathTime, onlyTrack);
      segs.length = bestI + 1;
      return;
    }
  }

  // Roll a local worm's predicted path back to `deathTime`: drop segments that
  // start after it and clamp the boundary segment to end exactly there (recomputing
  // its endpoint), so the rendered head freezes at the server's death position.
  function truncateOwnPath(playerId: any, deathTime: number, onlyTrack?: string) {
    if (typeof deathTime !== "number") return;
    for (const trackId of Object.keys(gameState.wormPathSegments)) {
      const segs = gameState.wormPathSegments[trackId];
      if (onlyTrack !== undefined && trackId !== onlyTrack) continue;
      if (!segs || !segs.length || segs[0].playerId !== playerId) continue;
      // Nothing to trim if we never predicted past the death time.
      if (segs[segs.length - 1].endTime <= deathTime + 1e-9) return;
      let i = segs.length - 1;
      while (i > 0 && segs[i].startTime >= deathTime) i--;
      const seg = segs[i];
      if (seg.startTime < deathTime && seg.endTime > deathTime && seg.duration > 0) {
        const dur = deathTime - seg.startTime;
        const pos = trajectoryUtil.followTrajectory(seg, dur);
        seg.duration = dur;
        seg.endTime = deathTime;
        if (pos) { seg.endX = pos.x; seg.endY = pos.y; seg.endDirection = pos.direction; }
      }
      segs.length = i + 1;
      return;
    }
  }

  function getRenderTime() {
    const t = anchorServerTime + (Date.now() - anchorWall) / 1000 - renderDelay;
    return t < 0 ? 0 : t;
  }

  // The estimated current server game time (no render delay). Used as the target
  // for predicting the local player's own worm in real time.
  function getServerTime() {
    const t = anchorServerTime + (Date.now() - anchorWall) / 1000;
    return t < 0 ? 0 : t;
  }

  return {
    setInitial, applyUpdate, getRenderTime, getServerTime, setLocalPlayerId, setLocalPlayerIds,
    getGameState: () => gameState,
    getServerLastSegment: (playerId: any) => serverLastSegment.get(playerId),
    setRenderDelay: (seconds: number) => { renderDelay = Math.max(0, Math.min(1, seconds)); },
    getRenderDelay: () => renderDelay,
  };
}
